import type jsPDF from 'jspdf';
import { BrandPdf, PDF_COLORS, type PdfValue } from '@/lib/pdf/brandPdf';
import type { PdfAssets } from '@/lib/pdf/pdfAssets';
import type { CallRecord, ExtensionReportData, MonthlyCallData } from '@/types';

/** PDFs for the Call Summary and Call Detail reports, in the app's style. */

const c = PDF_COLORS;

export function formatHMS(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return [hours, minutes, secs].map((part) => String(part).padStart(2, '0')).join(':');
}

function formatMinSec(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/** "2026-10-01" → "1 Oct 2026". */
export function formatPdfDay(value: string) {
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatGeneratedAt(date: Date) {
  return `Generated ${date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}, ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

/** "Reception<1001>" → "Reception (1001)". */
function partyText(value?: string) {
  const raw = (value ?? '').trim();
  if (!raw) return '-';
  const match = /^(.*?)\s*<([^<>]*)>$/.exec(raw);
  if (!match) return raw;
  const [, name, number] = match;
  return name && name !== number ? `${name} (${number})` : number;
}

const missedCell = (count: number): PdfValue =>
  count > 0 ? { text: String(count), color: c.red } : String(count);

function monthlyChart(doc: BrandPdf, months: MonthlyCallData[]) {
  doc.barChart({
    title: 'Monthly activity',
    meta: 'Inbound and outbound calls per month',
    series: [
      { label: 'Inbound', color: c.accent },
      { label: 'Outbound', color: c.indigo },
    ],
    groups: months.map((month) => ({
      label: month.month,
      values: [month.inboundTotal, month.outboundTotal],
    })),
  });
}

function monthlyTable(doc: BrandPdf, title: string, months: MonthlyCallData[], total?: PdfValue[]) {
  doc.table({
    title,
    meta: `${months.length} month${months.length === 1 ? '' : 's'}`,
    columns: [
      { header: 'Month', width: 24 },
      { header: 'In answered', width: 22, align: 'right' },
      { header: 'In missed', width: 19, align: 'right' },
      { header: 'In total', width: 17, align: 'right' },
      { header: 'Out answered', width: 23, align: 'right' },
      { header: 'Out missed', width: 20, align: 'right' },
      { header: 'Out total', width: 18, align: 'right' },
      { header: 'Talk time', width: 22, align: 'right' },
    ],
    rows: months.map((month) => [
      month.month,
      String(month.inboundAnswered),
      missedCell(month.inboundNoAnswer),
      { text: String(month.inboundTotal), bold: true, color: c.ink },
      String(month.outboundAnswered),
      missedCell(month.outboundNoAnswer),
      { text: String(month.outboundTotal), bold: true, color: c.ink },
      formatHMS(month.totalTalkDuration),
    ]),
    total,
  });
}

export function buildCallSummaryPdf(input: {
  report: ExtensionReportData;
  customerName?: string;
  assets?: PdfAssets;
  now?: Date;
}): jsPDF {
  const { report } = input;
  const { summary } = report;
  const name = report.extension.display_name || report.extension.username || 'Unknown';
  const doc = new BrandPdf({
    orientation: 'portrait',
    assets: input.assets,
    reportName: 'Call summary',
    title: `${report.extension.number} - ${name}`,
    subtitle: `${formatPdfDay(report.period.startDate)} – ${formatPdfDay(report.period.endDate)}`,
    customerName: input.customerName,
    generated: formatGeneratedAt(input.now ?? new Date()),
  });

  doc.statTiles([
    {
      label: 'Total calls',
      value: String(summary.inboundTotal + summary.outboundTotal),
      caption: 'Inbound + outbound',
      featured: true,
    },
    {
      label: 'Inbound',
      value: String(summary.inboundTotal),
      caption: `${summary.inboundAnswered} answered · ${summary.inboundNoAnswer} missed`,
      dot: c.accent,
    },
    {
      label: 'Outbound',
      value: String(summary.outboundTotal),
      caption: `${summary.outboundAnswered} answered · ${summary.outboundNoAnswer} missed`,
      dot: c.indigo,
    },
    {
      label: 'Talk time',
      value: formatHMS(summary.totalTalkDuration),
      caption: `Average ${formatMinSec(summary.averageCallDuration)} per call`,
    },
  ]);

  if (report.monthlyData.length > 0) {
    monthlyChart(doc, report.monthlyData);
    monthlyTable(doc, 'Monthly breakdown', report.monthlyData, [
      'Total',
      String(summary.inboundAnswered),
      missedCell(summary.inboundNoAnswer),
      { text: String(summary.inboundTotal), color: c.accentDark },
      String(summary.outboundAnswered),
      missedCell(summary.outboundNoAnswer),
      { text: String(summary.outboundTotal), color: c.accentDark },
      formatHMS(summary.totalTalkDuration),
    ]);
  }
  return doc.finish();
}

function isMissed(record: CallRecord) {
  const disposition = record.disposition?.toLowerCase() || '';
  return disposition.includes('no answer') || disposition.includes('noanswer') || disposition.includes('missed');
}

function statusColor(record: CallRecord) {
  const disposition = record.disposition?.toLowerCase() || '';
  if (isMissed(record) || disposition.includes('busy')) return c.orange;
  if (disposition.includes('answer')) return c.green;
  return c.muted;
}

export function buildCallDetailPdf(input: {
  extensionLabel: string;
  startDate: string;
  endDate: string;
  records: CallRecord[];
  monthlyData: MonthlyCallData[];
  formatTime: (record: CallRecord) => string;
  customerName?: string;
  assets?: PdfAssets;
  now?: Date;
}): jsPDF {
  const { records } = input;
  const missed = records.filter(isMissed).length;
  const talkSeconds = records.reduce((sum, record) => sum + (record.talk_duration || 0), 0);
  const answered = records.filter((record) => (record.talk_duration || 0) > 0);
  const average = answered.length > 0 ? Math.round(talkSeconds / answered.length) : 0;
  const inbound = records.filter((record) => record.call_type === 'Inbound').length;
  const outbound = records.filter((record) => record.call_type === 'Outbound').length;

  const doc = new BrandPdf({
    orientation: 'landscape',
    assets: input.assets,
    reportName: 'Call detail',
    title: input.extensionLabel,
    subtitle: `${formatPdfDay(input.startDate)} – ${formatPdfDay(input.endDate)}  ·  ${records.length} call${
      records.length === 1 ? '' : 's'
    }`,
    customerName: input.customerName,
    generated: formatGeneratedAt(input.now ?? new Date()),
  });

  doc.statTiles([
    { label: 'Total calls', value: String(records.length), caption: 'All directions', featured: true },
    { label: 'Inbound', value: String(inbound), dot: c.accent, caption: 'Calls received' },
    { label: 'Outbound', value: String(outbound), dot: c.indigo, caption: 'Calls made' },
    { label: 'Missed / failed', value: String(missed), dot: c.red, caption: 'Not answered' },
    { label: 'Talk time', value: formatHMS(talkSeconds), caption: 'Total on calls' },
    { label: 'Average per call', value: formatHMS(average), caption: 'Answered calls' },
  ]);

  if (input.monthlyData.length > 0) {
    monthlyChart(doc, input.monthlyData);
    monthlyTable(doc, 'Monthly summary', input.monthlyData);
  }

  doc.table({
    title: 'Calls',
    meta: `${records.length} call${records.length === 1 ? '' : 's'}`,
    columns: [
      { header: 'Date & time', width: 46 },
      { header: 'Type', width: 22 },
      { header: 'From', width: 66 },
      { header: 'To', width: 66 },
      { header: 'Status', width: 30 },
      { header: 'Talk time', width: 22, align: 'right' },
    ],
    rows: records.map((record) => [
      input.formatTime(record),
      {
        text: record.call_type,
        badge: true,
        color:
          record.call_type === 'Inbound'
            ? c.accentDark
            : record.call_type === 'Outbound'
              ? c.indigoDark
              : c.muted,
      },
      partyText(record.call_from),
      partyText(record.call_to),
      { text: record.disposition || 'Unknown', color: statusColor(record), badge: true },
      formatHMS(record.talk_duration || 0),
    ]),
    total: ['Total', '', '', '', '', { text: formatHMS(talkSeconds), color: c.accentDark }],
  });
  return doc.finish();
}
